import { supabase } from './supabase';
import { BrandData } from '@/context/BrandContext';
import { slugify } from './slug';

export interface Brand {
  id: string;
  workspace_id: string;
  name: string;
  slug: string;
  status: 'live' | 'private' | 'draft';
  industry: string;
  personality: string[];
  colors: {
    primary: string;
    secondary: string;
    accent: string;
  };
  typography: {
    primary: string;
    secondary: string;
  };
  mission: string;
  vision: string;
  values: string[];
  sections: any[];
  target_audience?: string[];
  moodboard_images?: string[];
  logo_url?: string;
  thumbnail_url?: string;
  description?: string;
  image_url?: string;
  story_description?: string;
  story_image_url?: string;
  website_url?: string;
  dark_logo_url?: string;
  favicon_url?: string;
  global_title_font?: string;
  global_text_font?: string;
  global_title_font_color?: string;
  global_text_font_color?: string;
  created_by?: string;
  created_at: string;
  updated_at: string;
}

export const brandService = {
  async getBrandsByWorkspace(workspaceId: string): Promise<Brand[]> {
    const { data, error } = await supabase
      .from('brands')
      .select('*')
      .eq('workspace_id', workspaceId)
      .order('created_at', { ascending: false });

    if (error) throw error;
    return data || [];
  },

  async getBrandById(brandId: string): Promise<Brand | null> {
    const { data, error } = await supabase
      .from('brands')
      .select('*')
      .eq('id', brandId)
      .maybeSingle();

    if (error) throw error;
    return data;
  },

  async getBrandBySlug(workspaceId: string, slug: string): Promise<Brand | null> {
    const { data, error } = await supabase
      .from('brands')
      .select('*')
      .eq('workspace_id', workspaceId)
      .eq('slug', slug)
      .maybeSingle();

    if (error) throw error;
    return data;
  },

  async createBrand(
    workspaceId: string,
    brandData: BrandData,
    sections: any[],
    userId: string
  ): Promise<Brand> {
    const slug = slugify(brandData.name);

    const { data, error } = await supabase
      .from('brands')
      .insert({
        workspace_id: workspaceId,
        name: brandData.name,
        slug: slug,
        industry: brandData.industry || '',
        personality: brandData.personality || [],
        colors: brandData.colors,
        typography: brandData.typography,
        mission: brandData.mission || '',
        vision: brandData.vision || '',
        values: brandData.values || [],
        sections: sections,
        target_audience: brandData.targetAudience || [],
        moodboard_images: brandData.moodboardImages || [],
        logo_url: brandData.brandLogo || null,
        thumbnail_url: brandData.brandThumbnail || null,
        description: brandData.brandDescription || null,
        image_url: brandData.brandImage || null,
        story_description: brandData.brandStoryDescription || null,
        story_image_url: brandData.brandStoryImage || null,
        website_url: brandData.brandUrl || null,
        dark_logo_url: brandData.primaryDarkLogo || null,
        favicon_url: brandData.favicon || null,
        global_title_font: brandData.globalTitleFont || null,
        global_text_font: brandData.globalTextFont || null,
        global_title_font_color: brandData.globalTitleFontColor || null,
        global_text_font_color: brandData.globalTextFontColor || null,
        created_by: userId,
      })
      .select()
      .single();

    if (error) throw error;
    return data;
  },

  async updateBrand(
    brandId: string,
    brandData: BrandData,
    sections: any[]
  ): Promise<Brand> {
    const { data, error } = await supabase
      .from('brands')
      .update({
        name: brandData.name,
        industry: brandData.industry || '',
        personality: brandData.personality || [],
        colors: brandData.colors,
        typography: brandData.typography,
        mission: brandData.mission || '',
        vision: brandData.vision || '',
        values: brandData.values || [],
        sections: sections,
        target_audience: brandData.targetAudience || [],
        moodboard_images: brandData.moodboardImages || [],
        logo_url: brandData.brandLogo || null,
        thumbnail_url: brandData.brandThumbnail || null,
        description: brandData.brandDescription || null,
        image_url: brandData.brandImage || null,
        story_description: brandData.brandStoryDescription || null,
        story_image_url: brandData.brandStoryImage || null,
        website_url: brandData.brandUrl || null,
        dark_logo_url: brandData.primaryDarkLogo || null,
        favicon_url: brandData.favicon || null,
        global_title_font: brandData.globalTitleFont || null,
        global_text_font: brandData.globalTextFont || null,
        global_title_font_color: brandData.globalTitleFontColor || null,
        global_text_font_color: brandData.globalTextFontColor || null,
      })
      .eq('id', brandId)
      .select()
      .single();

    if (error) throw error;
    return data;
  },

  async deleteBrand(brandId: string): Promise<void> {
    const { error } = await supabase
      .from('brands')
      .delete()
      .eq('id', brandId);

    if (error) throw error;
  },

  async updateBrandStatus(
    brandId: string,
    status: 'live' | 'private' | 'draft'
  ): Promise<Brand> {
    const { data, error } = await supabase
      .from('brands')
      .update({ status })
      .eq('id', brandId)
      .select()
      .single();

    if (error) throw error;
    return data;
  },
};
